/**
 * DmsTab — @flaxitup Instagram inbox. Left: conversation list.
 * Right: transcript + Claude-draft + send.
 */
import { useState, useEffect } from 'react';
import { Loader2, MessageCircle, Send, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/charts/EmptyState';
import { cn } from '@/lib/utils';
import { useConversations, useMessages, useDraftReply, useSendReply, type Conversation } from '@/hooks/useDms';

export function DmsTab() {
  const convs = useConversations();
  const [openId, setOpenId] = useState<string | null>(null);

  const rows = convs.data ?? [];
  if (convs.isLoading) return <Skeleton className="h-96 w-full" />;
  if (rows.length === 0) {
    return (
      <EmptyState
        title="No DMs synced yet"
        description="meta-dm-sync runs every 15 min once the token has instagram_manage_messages scope. Add that scope in the app's use case, refresh the token, and inbox will populate."
      />
    );
  }
  const active = rows.find((c) => c.id === openId) ?? null;

  return (
    <div className="grid gap-3 lg:grid-cols-[320px_minmax(0,1fr)]">
      <ConversationList rows={rows} openId={openId} onOpen={setOpenId} />
      <ConversationView conv={active} />
    </div>
  );
}

function ConversationList({ rows, openId, onOpen }: { rows: Conversation[]; openId: string | null; onOpen: (id: string) => void }) {
  return (
    <div className="space-y-1">
      {rows.map((c) => (
        <button
          key={c.id}
          type="button"
          onClick={() => onOpen(c.id)}
          className={cn(
            'flex w-full items-center gap-2 rounded-md border p-2 text-left transition-colors',
            openId === c.id ? 'border-violet-500 bg-violet-500/5' : 'border-border/50 hover:border-border',
          )}
        >
          {c.participant_profile_pic ? (
            <img src={c.participant_profile_pic} alt="" referrerPolicy="no-referrer" className="h-8 w-8 shrink-0 rounded-full object-cover" />
          ) : (
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted text-2xs uppercase text-muted-foreground">
              {(c.participant_username ?? '?').slice(0, 2)}
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div className="truncate text-xs font-medium">
              @{c.participant_username ?? c.participant_id ?? 'unknown'}
            </div>
            <div className="text-2xs text-muted-foreground tabular-nums">
              {c.updated_time ? new Date(c.updated_time).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'}
            </div>
          </div>
          {c.unread_count > 0 && (
            <span className="rounded-full bg-rose-500/10 px-1.5 py-0.5 text-2xs text-rose-500">{c.unread_count}</span>
          )}
        </button>
      ))}
    </div>
  );
}

function ConversationView({ conv }: { conv: Conversation | null }) {
  const msgs = useMessages(conv?.id ?? null);
  const draft = useDraftReply();
  const send = useSendReply();
  const [replyText, setReplyText] = useState('');
  const [replyId, setReplyId] = useState<string | null>(null);

  useEffect(() => { setReplyText(''); setReplyId(null); }, [conv?.id]);

  if (!conv) {
    return (
      <div className="flex items-center justify-center rounded-md border border-dashed border-border/50 bg-muted/20 p-8 text-xs text-muted-foreground">
        <MessageCircle className="mr-2 h-4 w-4" /> Pick a conversation
      </div>
    );
  }

  async function draftReply() {
    if (!conv) return;
    const r = await draft.mutateAsync(conv.id);
    setReplyText(r.draft);
    setReplyId(r.reply_id);
  }

  async function sendReply() {
    if (!conv?.participant_id || !replyText.trim()) return;
    await send.mutateAsync({
      conversation_id: conv.id,
      recipient_id: conv.participant_id,
      text: replyText,
      reply_id: replyId ?? undefined,
    });
    setReplyText('');
    setReplyId(null);
  }

  return (
    <Card>
      <CardContent className="flex h-[500px] flex-col p-3">
        <div className="mb-2 flex items-center gap-2 border-b border-border/40 pb-2">
          {conv.participant_profile_pic && (
            <img src={conv.participant_profile_pic} alt="" referrerPolicy="no-referrer" className="h-6 w-6 rounded-full object-cover" />
          )}
          <div className="text-sm font-medium">@{conv.participant_username ?? conv.participant_id ?? 'unknown'}</div>
          <a
            href={`https://www.instagram.com/${conv.participant_username ?? ''}/`}
            target="_blank" rel="noreferrer"
            className="ml-auto text-2xs text-violet-500 hover:underline"
          >
            open profile
          </a>
        </div>

        <div className="flex-1 space-y-2 overflow-y-auto pr-1">
          {msgs.isLoading ? (
            <Skeleton className="h-40 w-full" />
          ) : (msgs.data ?? []).map((m) => (
            <div key={m.id} className={cn('flex', m.is_from_me ? 'justify-end' : 'justify-start')}>
              <div className={cn(
                'max-w-[80%] rounded-2xl px-3 py-1.5 text-sm',
                m.is_from_me ? 'bg-violet-500 text-white' : 'bg-muted text-foreground',
              )}>
                {m.message_text ?? <span className="text-2xs opacity-60">(non-text message)</span>}
                <div className={cn('mt-0.5 text-2xs tabular-nums', m.is_from_me ? 'text-white/70' : 'text-muted-foreground')}>
                  {new Date(m.created_time).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-2 space-y-2 border-t border-border/40 pt-2">
          <textarea
            rows={2}
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            placeholder="Reply — or click Draft with Claude"
            className="w-full rounded-md border border-border/50 bg-background px-2.5 py-1.5 text-sm"
          />
          <div className="flex items-center gap-1.5">
            <Button size="sm" variant="outline" onClick={draftReply} disabled={draft.isPending}>
              {draft.isPending ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Sparkles className="mr-1.5 h-3.5 w-3.5" />}
              Draft with Claude
            </Button>
            <Button size="sm" onClick={sendReply} disabled={send.isPending || !replyText.trim() || !conv.participant_id}>
              {send.isPending ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Send className="mr-1.5 h-3.5 w-3.5" />}
              Send
            </Button>
            {send.error && <span className="text-2xs text-rose-500">{(send.error as Error).message}</span>}
            {!conv.participant_id && <span className="text-2xs text-amber-500">missing recipient_id — DM sync may need instagram_manage_messages scope</span>}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
